import { Trans, useLingui } from "@lingui/react/macro";
import { DROK_AUDIT_ACTIONS } from "@rakazo/contracts";
import { Button, NativeSelect, NativeSelectOption, Spinner } from "@rakazo/ui-web";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@rakazo/ui-web/components/ui/table";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useQueryUserId } from "../../lib/query";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";
import { auditActionLabels } from "./audit-actions";

/** Who did what, newest first, with filters for the action and the affected user. */
export function AdminAuditPage() {
  const { i18n, t } = useLingui();
  const selfId = useQueryUserId();
  const [action, setAction] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const query = useInfiniteQuery({
    queryKey: ["drok", `user:${selfId ?? ""}`, "admin.audit", action, targetUserId],
    queryFn: ({ pageParam }) =>
      drokRpc.audit.list({
        action: action || undefined,
        targetUserId: targetUserId || undefined,
        cursor: pageParam,
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: selfId !== null,
  });
  const users = useInfiniteQuery({
    queryKey: ["drok", `user:${selfId ?? ""}`, "admin.audit-users"],
    queryFn: ({ pageParam }) => drokRpc.users.list({ cursor: pageParam, limit: 100 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: selfId !== null,
  });
  const events = query.data?.pages.flatMap((page) => page.events) ?? [];
  const label = (name: string): string => {
    const descriptor = auditActionLabels()[name];
    return descriptor ? i18n._(descriptor) : name;
  };
  const when = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.locale || "en", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  return (
    <section data-testid="admin-audit">
      <div className="mb-4 flex flex-wrap gap-2">
        <NativeSelect
          aria-label={t`Filter by action`}
          value={action}
          onChange={(e) => setAction(e.target.value)}
        >
          <NativeSelectOption value="">{t`All actions`}</NativeSelectOption>
          {DROK_AUDIT_ACTIONS.map((name) => (
            <NativeSelectOption key={name} value={name}>
              {label(name)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label={t`Filter by user`}
          value={targetUserId}
          onChange={(e) => setTargetUserId(e.target.value)}
        >
          <NativeSelectOption value="">{t`All users`}</NativeSelectOption>
          {(users.data?.pages.flatMap((page) => page.users) ?? []).map((user) => (
            <NativeSelectOption key={user.id} value={user.id}>
              {user.email}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
      {query.isPending ? (
        <div className="grid place-items-center p-8">
          <Spinner aria-label={t`Loading…`} />
        </div>
      ) : query.isError ? (
        <div role="alert" className="rounded-xl bg-muted/50 p-4 text-sm">
          <p className="text-destructive">{errorText(query.error)}</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void query.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      ) : events.length === 0 ? (
        <p className="rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">
          <Trans>No events yet</Trans>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Trans>When</Trans>
                </TableHead>
                <TableHead>
                  <Trans>Action</Trans>
                </TableHead>
                <TableHead>
                  <Trans>By</Trans>
                </TableHead>
                <TableHead>
                  <Trans>User</Trans>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id} data-testid="admin-audit-row">
                  <TableCell className="whitespace-nowrap">{when(event.createdAt)}</TableCell>
                  <TableCell>{label(event.action)}</TableCell>
                  <TableCell>{event.actorEmail ?? event.actor}</TableCell>
                  <TableCell>{event.targetEmail ?? event.targetUserId ?? ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {query.hasNextPage ? (
            <Button
              className="mt-3"
              variant="outline"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              <Trans>Load more</Trans>
            </Button>
          ) : null}
        </div>
      )}
    </section>
  );
}
